import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, Crown, Newspaper, Image as ImageIcon, LayoutGrid, Search, Star, ShieldAlert,
  MapPin, Scale, Leaf, Landmark, HandCoins, UserPlus, UserCheck, X, Loader2, Heart,
  Gavel, Sparkles, ChevronRight, Link2, HelpCircle, UserRound,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import CommunityProfileEditor from "@/components/menu/CommunityProfileEditor";

/* First-visit tour — explains the hub once, then lives behind "How it works". */
const TOUR_KEY = "nc_social_tour_v1";
const TOUR_STEPS = [
  { title: "Welcome to the Community", body: "This is Bornwood's society layer — every citizen, crew, leaderboard and headline across the network, browsable right from the main menu. Sixty seconds and you'll know it like a local." },
  { title: "The whole city, or one server", body: "The hub opens on the ENTIRE NETWORK — an aggregate of every server at once. Use the selector top-right to zoom into a single server's society whenever you want the local picture." },
  { title: "The five tabs", body: "CITIZENS — player files, follows and reviews. CREWS — found or join a crew. LEADERBOARDS — richest, most wanted, top dealers. CITY WIRE — the live feed of what the server's been through. GALLERY — phone snaps from the streets." },
  { title: "Make it yours", body: "Hit MY PROFILE (top right) to set your avatar, banner, bio and privacy — uploads are safety-scanned before they go live. Link your in-game playercode from your citizen file so your game life and community identity connect." },
];

/* NEW CITIZEN COMMUNITY — the main-menu social hub. Per-server society:
   citizen files, crews, leaderboards, the live city wire and the snap gallery.
   Browsing is public; follows/reviews/crews use the hub account (web preview:
   sign in on the site; in-game the hub session rides along when present). */

const HUES = { p1: 210, p2: 20, p3: 150, p4: 280, p5: 340, p6: 95 };
function Portrait({ id, className = "w-12 h-[60px]" }) {
  const h = HUES[id] ?? 210;
  return (
    <div className={`${className} overflow-hidden shrink-0`} style={{ background: `linear-gradient(160deg, hsl(${h},42%,32%), hsl(${h},48%,15%))` }}>
      <svg viewBox="0 0 40 50" className="w-full h-full">
        <circle cx="20" cy="17" r="8" fill={`hsl(${h},32%,${58 + (h % 3) * 6}%)`} />
        <path d="M5 50 C5 35 35 35 35 50 Z" fill={`hsl(${h},30%,${42 + (h % 4) * 5}%)`} />
        <circle cx="17" cy="15.5" r="1" fill="hsl(0,0%,8%)" /><circle cx="23" cy="15.5" r="1" fill="hsl(0,0%,8%)" />
      </svg>
    </div>
  );
}

const ago = (at) => {
  const s = (Date.now() - new Date(at).getTime()) / 1000;
  if (!isFinite(s) || s < 0) return "now";
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const WIRE_META = {
  court: { Icon: Scale, color: "#e8b93e" }, verdict: { Icon: Gavel, color: "#ef4444" },
  property: { Icon: Landmark, color: "#4ade80" }, pawn: { Icon: HandCoins, color: "#f59e0b" },
  citizen: { Icon: Sparkles, color: "#38bdf8" }, turf: { Icon: MapPin, color: "#a78bfa" },
  crew: { Icon: Users, color: "#2E6BFF" }, crime: { Icon: ShieldAlert, color: "#f87171" },
};

const Stars = ({ n, size = "w-3 h-3" }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? "text-amber-300 fill-amber-300" : "text-white/15"}`} />)}
  </span>
);

const BOARD_DEFS = [
  ["richest", "Richest citizens", Crown, "#e8b93e"],
  ["dealers", "Top dealers", Leaf, "#4ade80"],
  ["tycoons", "Property tycoons", Landmark, "#38bdf8"],
  ["reviewed", "Most reviewed", Star, "#f59e0b"],
  ["wanted", "Most wanted", ShieldAlert, "#ef4444"],
  ["crews", "Richest crews", Users, "#a78bfa"],
];
const MEDAL = ["#e8b93e", "#a1a1aa", "#b45309"];

export function SocialHubPanel({ onBack, audio, accent = "#2E6BFF" }) {
  const [tourOpen, setTourOpen] = useState(() => !localStorage.getItem(TOUR_KEY));
  const [tourStep, setTourStep] = useState(0);
  const [editProfile, setEditProfile] = useState(false);
  const [servers, setServers] = useState([]);
  const [serverId, setServerId] = useState("");
  const [tab, setTab] = useState("citizens");
  const [q, setQ] = useState("");
  const [citizens, setCitizens] = useState(null);
  const [crews, setCrews] = useState(null);
  const [boards, setBoards] = useState(null);
  const [wire, setWire] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [profilePc, setProfilePc] = useState(null);
  const [profile, setProfile] = useState(null);
  const [me, setMe] = useState(null);
  const [crewForm, setCrewForm] = useState({ name: "", tag: "", color: "#2E6BFF", motto: "" });
  const [showCrewForm, setShowCrewForm] = useState(false);
  const [reviewDraft, setReviewDraft] = useState({ stars: 5, body: "" });
  const [linkPc, setLinkPc] = useState("");
  const [busy, setBusy] = useState(false);
  const [net, setNet] = useState(null); // entire-network aggregate
  const isNet = serverId === "__net";
  const hasSession = !!localStorage.getItem("nc_token");

  useEffect(() => { if (hasSession) api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, [hasSession]);

  useEffect(() => {
    api.get("/nc/servers").then((r) => {
      const list = r.data.servers || [];
      setServers(list);
      // default landing: the ENTIRE NETWORK aggregate — players see the whole city first
      setServerId((cur) => cur || "__net");
    }).catch(() => setServers([]));
  }, []);

  const loadTab = useCallback(() => {
    if (!serverId) return;
    if (isNet) { setNet(null); api.get("/nc/community/network", { params: { q } }).then((r) => setNet(r.data)).catch(() => setNet(null)); return; }
    if (tab === "citizens") { setCitizens(null); api.get(`/nc/servers/${serverId}/club/citizens`, { params: { q } }).then((r) => setCitizens(r.data.citizens)).catch(() => setCitizens([])); }
    if (tab === "crews") { setCrews(null); api.get(`/nc/servers/${serverId}/club/crews`).then((r) => setCrews(r.data.crews)).catch(() => setCrews([])); }
    if (tab === "boards") { setBoards(null); api.get(`/nc/servers/${serverId}/club/boards`).then((r) => setBoards(r.data.boards)).catch(() => setBoards({})); }
    if (tab === "gallery") { setGallery(null); api.get(`/nc/servers/${serverId}/club/gallery`).then((r) => setGallery(r.data.snaps)).catch(() => setGallery([])); }
  }, [serverId, tab, q, isNet]);
  useEffect(() => { loadTab(); }, [loadTab]);

  // wire auto-refreshes — it's the city's heartbeat
  useEffect(() => {
    if (tab !== "wire" || !serverId || isNet) return;
    const pull = () => api.get(`/nc/servers/${serverId}/club/wire`).then((r) => setWire(r.data.events)).catch(() => {});
    pull();
    const t = setInterval(pull, 15000);
    return () => clearInterval(t);
  }, [tab, serverId, isNet]);

  const openProfile = (pc) => {
    audio?.tick();
    setProfilePc(pc); setProfile(null);
    api.get(`/nc/servers/${serverId}/club/citizen/${pc}`).then((r) => setProfile(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setProfilePc(null); });
  };

  const needAuth = () => { toast.error("Sign in on the web hub to do that — your session carries into the menu"); return true; };

  const follow = async () => {
    if (!hasSession) return needAuth();
    try {
      const r = await api.post(`/nc/servers/${serverId}/club/follow/${profilePc}`);
      setProfile((p) => ({ ...p, following: r.data.following, followers: p.followers + (r.data.following ? 1 : -1) }));
      audio?.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const postReview = async () => {
    if (!hasSession) return needAuth();
    if (reviewDraft.body.trim().length < 3 || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/club/review/${profilePc}`, reviewDraft);
      toast.success(r.data.message);
      setReviewDraft({ stars: 5, body: "" });
      openProfile(profilePc);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const linkCitizen = async () => {
    if (!hasSession) return needAuth();
    const pc = parseInt(linkPc, 10);
    if (!pc) return;
    try {
      const r = await api.post(`/nc/servers/${serverId}/club/link`, { playercode: pc });
      toast.success(r.data.message);
      setLinkPc("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const crewAct = async (crew, action) => {
    if (!hasSession) return needAuth();
    try {
      const r = await api.post(`/nc/servers/${serverId}/club/crews/${crew.id}/${action}`);
      toast.success(r.data.message);
      audio?.click();
      loadTab();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createCrew = async () => {
    if (!hasSession) return needAuth();
    if (crewForm.name.trim().length < 3 || crewForm.tag.trim().length < 2 || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/club/crews`, crewForm);
      toast.success(r.data.message);
      setShowCrewForm(false); setCrewForm({ name: "", tag: "", color: "#2E6BFF", motto: "" });
      loadTab();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const TABS = [["citizens", "Citizens", LayoutGrid], ["crews", "Crews", Users], ["boards", "Leaderboards", Crown], ["wire", "City Wire", Newspaper], ["gallery", "Gallery", ImageIcon]];
  const serverName = useMemo(() => servers.find((s) => s.id === serverId)?.name || "", [servers, serverId]);

  return (
    <div data-testid="social-hub" className="absolute inset-0 flex flex-col" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      {/* header */}
      <div className="flex items-center gap-4 px-6 sm:px-10 pt-20 flex-wrap">
        <button data-testid="club-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-zinc-100 transition-colors">‹ Back</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50">Community</h2>
        <span className="font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: accent }}>// {isNet ? "the entire network" : `the ${serverName || "…"} society`}</span>
        <div className="ml-auto flex items-center gap-2">
          {me && (
            <button data-testid="club-my-profile" onClick={() => { setEditProfile(true); audio?.tick(); }}
              className="font-mono text-[9px] font-bold tracking-[0.2em] uppercase border border-white/20 hover:border-white/50 text-zinc-300 hover:text-white px-3 py-2 transition-colors flex items-center gap-1.5">
              <UserRound className="w-3 h-3" /> My Profile
            </button>
          )}
          <button data-testid="club-tour-open" onClick={() => { setTourStep(0); setTourOpen(true); }}
            className="font-mono text-[9px] font-bold tracking-[0.2em] uppercase border px-3 py-2 transition-colors flex items-center gap-1.5"
            style={{ borderColor: `${accent}55`, color: accent }}>
            <HelpCircle className="w-3 h-3" /> How it works
          </button>
          <select data-testid="club-server" value={serverId} onChange={(e) => { setServerId(e.target.value); setProfilePc(null); audio?.tick(); }}
            className="bg-black/50 border border-white/15 outline-none px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 max-w-64">
            <option value="__net" className="bg-black">◈ Entire Network</option>
            {servers.map((s) => <option key={s.id} value={s.id} className="bg-black">{s.name}{s.official ? " ★" : ""}</option>)}
          </select>
        </div>
      </div>

      {/* tab bar */}
      <div className="px-6 sm:px-10 mt-4 flex gap-1 border-b border-white/10">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`club-tab-${id}`} onClick={() => { setTab(id); audio?.tick(); }}
            className={`flex items-center gap-2 px-4 sm:px-5 py-3 font-mono text-[10px] font-bold tracking-[0.25em] uppercase border-b-2 transition-colors ${tab === id ? "text-zinc-50" : "text-zinc-500 hover:text-zinc-200 border-transparent"}`}
            style={tab === id ? { borderBottomColor: accent, backgroundColor: `${accent}10` } : {}}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {/* body */}
      <div className="flex-1 min-h-0 flex px-6 sm:px-10 py-5 gap-5">
        <div className="flex-1 min-w-0 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin", scrollbarColor: `${accent}44 transparent` }}>

          {/* ENTIRE NETWORK — cross-server aggregate view */}
          {isNet && <NetworkView net={net} q={q} setQ={setQ} accent={accent} reload={loadTab} />}

          {/* CITIZENS */}
          {!isNet && tab === "citizens" && (
            <div className="flex flex-col gap-4">
              <div className="relative w-72">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input data-testid="club-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="SEARCH CITIZENS…"
                  className="w-full bg-black/50 border border-white/15 focus:border-white/40 outline-none pl-9 pr-3 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-200 placeholder:text-zinc-600" />
              </div>
              {!citizens ? <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Reading the registry…</p> :
                citizens.length === 0 ? <p data-testid="club-no-citizens" className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase">No citizens on this server yet — the city awaits its first arrivals.</p> : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {citizens.map((c, i) => (
                    <button key={c.playercode} data-testid={`club-citizen-${i}`} onClick={() => openProfile(c.playercode)}
                      className="group relative border border-white/10 bg-[#080a0f]/85 p-4 flex items-center gap-3 text-left transition-all hover:border-white/30 hover:-translate-y-0.5"
                      style={{ animation: `ncFadeUp 0.4s ease ${Math.min(i * 0.04, 0.6)}s both` }}>
                      <Portrait id={c.portrait} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-zinc-100 truncate">{c.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {c.crew && <span className="font-mono text-[8px] font-bold px-1 py-px border" style={{ color: c.crew.color, borderColor: `${c.crew.color}66` }}>[{c.crew.tag}]</span>}
                          <Stars n={c.review_avg} size="w-2.5 h-2.5" />
                          {c.review_count > 0 && <span className="font-mono text-[8px] text-zinc-500">{c.review_count}</span>}
                        </div>
                        <p className="font-mono text-[9px] text-zinc-500 mt-1">{money(c.bank)}</p>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-200 transition-colors shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CREWS */}
          {!isNet && tab === "crews" && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">Crews of {serverName || "the city"} · ranked by combined worth</p>
                <button data-testid="crew-create-open" onClick={() => { setShowCrewForm((s) => !s); audio?.tick(); }}
                  className="ml-auto font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-2 transition-colors"
                  style={{ borderColor: `${accent}66`, color: accent }}>
                  {showCrewForm ? "Cancel" : "+ Found a crew"}
                </button>
              </div>
              {showCrewForm && (
                <div data-testid="crew-form" className="border border-white/12 bg-[#080a0f]/90 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ animation: "ncFadeUp 0.25s ease both" }}>
                  <input data-testid="crew-name" value={crewForm.name} maxLength={40} onChange={(e) => setCrewForm({ ...crewForm, name: e.target.value })} placeholder="CREW NAME"
                    className="bg-black/60 border border-white/15 px-3 py-2.5 font-mono text-[11px] text-zinc-100 outline-none focus:border-white/40" />
                  <input data-testid="crew-tag" value={crewForm.tag} maxLength={4} onChange={(e) => setCrewForm({ ...crewForm, tag: e.target.value.toUpperCase() })} placeholder="TAG (2-4)"
                    className="bg-black/60 border border-white/15 px-3 py-2.5 font-mono text-[11px] text-zinc-100 outline-none focus:border-white/40" />
                  <input data-testid="crew-motto" value={crewForm.motto} maxLength={120} onChange={(e) => setCrewForm({ ...crewForm, motto: e.target.value })} placeholder="MOTTO — OPTIONAL"
                    className="sm:col-span-2 bg-black/60 border border-white/15 px-3 py-2.5 font-mono text-[11px] text-zinc-100 outline-none focus:border-white/40" />
                  <div className="flex items-center gap-2">
                    {["#2E6BFF", "#ef4444", "#4ade80", "#f59e0b", "#a78bfa", "#38bdf8"].map((c) => (
                      <button key={c} data-testid={`crew-color-${c.slice(1)}`} onClick={() => setCrewForm({ ...crewForm, color: c })}
                        className={`w-7 h-7 border-2 transition-transform hover:scale-110 ${crewForm.color === c ? "border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
                    ))}
                  </div>
                  <button data-testid="crew-create-btn" onClick={createCrew} disabled={busy || crewForm.name.trim().length < 3 || crewForm.tag.trim().length < 2}
                    className="font-mono text-[10px] font-bold tracking-[0.25em] uppercase text-black py-2.5 disabled:opacity-40" style={{ backgroundColor: crewForm.color }}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Found crew"}
                  </button>
                </div>
              )}
              {!crews ? <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Rounding up the crews…</p> :
                crews.length === 0 ? <p data-testid="crews-empty" className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase">No crews yet — found the first one and run this city.</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {crews.map((c, i) => {
                    const mine = me && c.members.some((m) => m.name === (me.name || ""));
                    return (
                      <div key={c.id} data-testid={`crew-card-${i}`} className="border border-white/10 bg-[#080a0f]/85 p-5 flex flex-col gap-3"
                        style={{ animation: `ncFadeUp 0.4s ease ${Math.min(i * 0.05, 0.5)}s both` }}>
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-sm px-2 py-1 border" style={{ color: c.color, borderColor: `${c.color}66`, backgroundColor: `${c.color}14` }}>[{c.tag}]</span>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm text-zinc-50 truncate">{c.name}</p>
                            {c.motto && <p className="font-mono text-[9px] text-zinc-500 italic truncate">"{c.motto}"</p>}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-mono text-[10px] text-zinc-300">{money(c.worth)}</p>
                            {c.stash > 0 && <p data-testid={`crew-stash-${i}`} className="font-mono text-[8px] text-emerald-400/90">stash {money(c.stash)}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {c.members.slice(0, 10).map((m, mi) => (
                            <span key={mi} title={m.name} className="w-7 h-7 border border-white/15 bg-black/60 flex items-center justify-center font-mono text-[9px] text-zinc-300">{(m.name || "C")[0]}</span>
                          ))}
                          {c.member_count > 10 && <span className="font-mono text-[9px] text-zinc-500">+{c.member_count - 10}</span>}
                          <span className="ml-auto font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">{c.member_count} member{c.member_count !== 1 ? "s" : ""} · led by {c.owner_name}</span>
                        </div>
                        <button data-testid={`crew-join-${i}`} onClick={() => crewAct(c, mine ? "leave" : "join")}
                          className={`mt-auto font-mono text-[9px] font-bold tracking-[0.25em] uppercase py-2.5 border transition-colors ${mine ? "border-red-500/40 text-red-400 hover:bg-red-500/10" : "text-black"}`}
                          style={mine ? {} : { backgroundColor: c.color, borderColor: c.color }}>
                          {mine ? "Leave crew" : "Join crew"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* LEADERBOARDS */}
          {!isNet && tab === "boards" && (
            !boards ? <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Counting the city's money…</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {BOARD_DEFS.map(([key, title, Icon, color]) => (
                  <div key={key} data-testid={`board-${key}`} className="border border-white/10 bg-[#080a0f]/85">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                      <Icon className="w-3.5 h-3.5" style={{ color }} />
                      <p className="font-mono text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-200">{title}</p>
                    </div>
                    <div className="p-2">
                      {(boards[key] || []).length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] px-3 py-4">Nobody on the board yet</p>}
                      {(boards[key] || []).map((r, i) => (
                        <button key={i} data-testid={`board-${key}-${i}`} onClick={() => r.pc && openProfile(r.pc)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${r.pc ? "hover:bg-white/[0.04]" : "cursor-default"}`}>
                          <span className="font-mono text-[10px] font-bold w-6 text-center" style={{ color: MEDAL[i] || "#52525b" }}>{i + 1}</span>
                          {key === "crews" && r.color ? <span className="w-2 h-2" style={{ backgroundColor: r.color }} /> : null}
                          <span className="flex-1 font-semibold text-[12px] text-zinc-200 truncate">{r.name}</span>
                          <span className="font-mono text-[10px] text-zinc-400 shrink-0">{typeof r.value === "number" ? money(r.value) : r.value}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* CITY WIRE */}
          {!isNet && tab === "wire" && (
            <div className="flex flex-col gap-1 max-w-3xl">
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live from {serverName || "the city"} — refresh 15s
              </p>
              {!wire ? <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Listening to the wire…</p> :
                wire.length === 0 ? <p data-testid="wire-empty" className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase">Quiet so far — verdicts, settlements, turf moves and new arrivals will land here.</p> :
                wire.map((e, i) => {
                  const meta = WIRE_META[e.kind] || WIRE_META.citizen;
                  return (
                    <div key={e.id} data-testid={`wire-event-${i}`} className="flex items-start gap-3 border-l-2 py-2.5 pl-4 pr-2 bg-[#080a0f]/60"
                      style={{ borderColor: meta.color, animation: `ncFadeUp 0.35s ease ${Math.min(i * 0.03, 0.5)}s both` }}>
                      <meta.Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: meta.color }} />
                      <p className="flex-1 font-mono text-[11px] text-zinc-200 leading-relaxed">{e.text}</p>
                      <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] shrink-0 mt-1">{ago(e.at)}</span>
                    </div>
                  );
                })}
            </div>
          )}

          {/* GALLERY */}
          {!isNet && tab === "gallery" && (
            !gallery ? <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Developing the city's film…</p> :
              gallery.length === 0 ? (
                <div data-testid="gallery-empty" className="border border-dashed border-white/15 p-10 text-center max-w-xl">
                  <ImageIcon className="w-6 h-6 text-zinc-600 mx-auto mb-3" />
                  <p className="font-mono text-[10px] text-zinc-500 tracking-[0.15em] uppercase leading-relaxed">No snaps on the wall yet.<br />Citizens share phone photos on Buzzer — the best of the city lands here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {gallery.map((g, i) => (
                    <figure key={g.id} data-testid={`snap-${i}`} className="group relative border border-white/10 bg-[#080a0f]/85 overflow-hidden"
                      style={{ animation: `ncFadeUp 0.4s ease ${Math.min(i * 0.04, 0.6)}s both` }}>
                      <img src={g.image_url} alt="" className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3 pt-8">
                        <p className="font-mono text-[9px] text-zinc-200 truncate">{g.caption}</p>
                        <p className="font-mono text-[8px] text-zinc-500 mt-0.5 flex items-center gap-2">@{g.handle}<span className="flex items-center gap-1"><Heart className="w-2.5 h-2.5" />{g.likes}</span><span className="ml-auto">{ago(g.at)}</span></p>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )
          )}
        </div>

        {/* CITIZEN FILE drawer */}
        {profilePc !== null && (
          <aside data-testid="club-profile" className="w-full md:w-[460px] shrink-0 border border-white/12 bg-[#070a10]/95 backdrop-blur-md flex flex-col overflow-hidden"
            style={{ animation: "ncSlideR 0.2s ease-out both" }}>
            <div className="h-[3px] w-full shrink-0" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
            {!profile ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4" style={{ scrollbarWidth: "thin" }}>
                <div className="flex items-start gap-4">
                  <Portrait id={profile.portrait} className="w-20 h-[100px] border border-white/15" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p data-testid="club-profile-name" className="font-bold text-lg text-zinc-50 leading-tight truncate">{profile.name}</p>
                      <button data-testid="club-profile-close" onClick={() => setProfilePc(null)} className="text-zinc-500 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                    <p className="font-mono text-[9px] text-zinc-500">@{profile.handle} · #{profile.playercode}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {profile.crew && <span className="font-mono text-[8px] font-bold px-1.5 py-0.5 border" style={{ color: profile.crew.color, borderColor: `${profile.crew.color}66` }}>[{profile.crew.tag}] {profile.crew.name}</span>}
                      {profile.joined && <span className="font-mono text-[8px] text-zinc-600">citizen since {String(profile.joined).slice(0, 10)}</span>}
                    </div>
                    <button data-testid="club-follow" onClick={follow}
                      className={`mt-2.5 font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${profile.following ? "border-white/20 text-zinc-300" : "text-black"}`}
                      style={profile.following ? {} : { backgroundColor: accent, borderColor: accent }}>
                      {profile.following ? <UserCheck className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
                      {profile.following ? "Following" : "Follow"} · {profile.followers}
                    </button>
                  </div>
                </div>

                {profile.bans.length > 0 && (
                  <div data-testid="club-profile-bans" className="border border-red-500/40 bg-red-500/10 p-3 flex flex-col gap-1">
                    {profile.bans.map((b, i) => (
                      <p key={i} className="font-mono text-[9px] text-red-200 flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5 text-red-400 shrink-0" /> BANNED — {b.reason}</p>
                    ))}
                  </div>
                )}
                {profile.warrants_active > 0 && (
                  <p data-testid="club-profile-warrant" className="font-mono text-[9px] text-red-300 flex items-center gap-2 border border-red-500/30 bg-red-500/5 px-3 py-2">
                    <Gavel className="w-3.5 h-3.5" /> {profile.warrants_active} ACTIVE WARRANT{profile.warrants_active > 1 ? "S" : ""} — approach with caution
                  </p>
                )}

                <div className="grid grid-cols-4 gap-2">
                  {[["Bank", money(profile.worth.bank)], ["Property", money(profile.worth.property_value)],
                    ["Buzzers", profile.buzzers], ["Rating", profile.reviews.count ? `${profile.reviews.avg}★` : "—"]].map(([k, v]) => (
                    <div key={k} className="border border-white/10 bg-black/40 p-2.5 text-center">
                      <p className="font-bold text-sm text-zinc-100 truncate">{v}</p>
                      <p className="font-mono text-[7px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">{k}</p>
                    </div>
                  ))}
                </div>

                {profile.licence && (
                  <p className="font-mono text-[9px] text-zinc-400 flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2">
                    <Landmark className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                    Class {profile.licence.class} licence · {profile.licence.points} pts
                    {profile.licence.suspended && <span className="text-red-400 font-bold">SUSPENDED</span>}
                    {profile.licence.interlock && <span className="text-amber-300">INTERLOCK</span>}
                  </p>
                )}
                {profile.dealer && (
                  <p className="font-mono text-[9px] text-emerald-200 flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                    <Leaf className="w-3.5 h-3.5 shrink-0" /> Runs {profile.dealer.name} on GreenPages — {profile.dealer.rating}★ · {profile.dealer.sales} sales{profile.dealer.turfs > 0 ? ` · holds ${profile.dealer.turfs} corner${profile.dealer.turfs > 1 ? "s" : ""}` : ""}
                  </p>
                )}
                {profile.court.cases > 0 && (
                  <div className="border border-white/10 bg-black/40 px-3 py-2">
                    <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Court record — {profile.court.cases} case{profile.court.cases > 1 ? "s" : ""}, {profile.court.convictions} conviction{profile.court.convictions !== 1 ? "s" : ""}</p>
                    {profile.court.recent.map((c, i) => (
                      <p key={i} className="font-mono text-[9px] text-zinc-400 py-0.5">{c.docket} — <span className={c.outcome === "guilty" ? "text-red-400" : "text-zinc-300"}>{String(c.outcome).replace("_", " ")}</span></p>
                    ))}
                  </div>
                )}
                {profile.properties.length > 0 && (
                  <div className="border border-white/10 bg-black/40 px-3 py-2">
                    <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Deeds held</p>
                    {profile.properties.map((p) => (
                      <p key={p.key} className="font-mono text-[9px] text-zinc-300 py-0.5 flex items-center gap-2"><MapPin className="w-3 h-3 text-zinc-500" />{p.title} · {p.area} <span className="ml-auto text-zinc-500">{money(p.price)}</span></p>
                    ))}
                  </div>
                )}

                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-400 mb-2 flex items-center gap-2"><Star className="w-3 h-3 text-amber-300" /> Community reviews ({profile.reviews.count})</p>
                  {profile.reviews.list.slice(0, 5).map((r, i) => (
                    <div key={i} data-testid={`club-review-${i}`} className="border border-white/10 bg-black/30 p-2.5 mb-1.5">
                      <div className="flex items-center gap-2"><Stars n={r.stars} size="w-2.5 h-2.5" /><span className="font-mono text-[8px] text-zinc-500 ml-auto">{r.author_name} · {ago(r.at)}</span></div>
                      <p className="font-mono text-[10px] text-zinc-300 mt-1">{r.body}</p>
                    </div>
                  ))}
                  {hasSession && profile.my_pc !== profile.playercode && (
                    <div data-testid="club-review-form" className="border border-white/10 bg-black/40 p-3 flex flex-col gap-2 mt-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <button key={i} data-testid={`club-star-${i}`} onClick={() => setReviewDraft({ ...reviewDraft, stars: i })}>
                            <Star className={`w-4 h-4 transition-colors ${i <= reviewDraft.stars ? "text-amber-300 fill-amber-300" : "text-white/15 hover:text-amber-200"}`} />
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input data-testid="club-review-input" value={reviewDraft.body} maxLength={280} onChange={(e) => setReviewDraft({ ...reviewDraft, body: e.target.value })}
                          placeholder="Deal with them? Leave a word…" className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-[10px] text-zinc-100 outline-none focus:border-white/40" />
                        <button data-testid="club-review-post" onClick={postReview} disabled={busy || reviewDraft.body.trim().length < 3}
                          className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-4 text-black disabled:opacity-40" style={{ backgroundColor: "#e8b93e" }}>Post</button>
                      </div>
                    </div>
                  )}
                </div>

                {hasSession && (
                  <div className="border-t border-white/10 pt-3 mt-1">
                    <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-600 mb-1.5 flex items-center gap-1.5"><Link2 className="w-3 h-3" /> Is this you? Link your citizen</p>
                    <div className="flex gap-2">
                      <input data-testid="club-link-input" value={linkPc} onChange={(e) => setLinkPc(e.target.value.replace(/\D/g, ""))} placeholder="PLAYERCODE"
                        className="w-32 bg-black/60 border border-white/15 px-3 py-2 font-mono text-[10px] text-zinc-100 outline-none focus:border-white/40" />
                      <button data-testid="club-link-btn" onClick={linkCitizen} className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] border border-white/20 text-zinc-300 px-3 py-2 hover:border-white/50 transition-colors">Link</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* in-menu profile editor — citizens never leave the game for the website */}
      {editProfile && <CommunityProfileEditor accent={accent} audio={audio} onClose={() => setEditProfile(false)} />}

      {/* first-visit tour / how-it-works overlay */}
      {tourOpen && (
        <div data-testid="club-tour" className="absolute inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-6"
          onClick={() => { setTourOpen(false); localStorage.setItem(TOUR_KEY, "1"); }}>
          <div className="w-full max-w-md border border-white/15 bg-[#070a10] shadow-2xl" onClick={(e) => e.stopPropagation()}
            style={{ animation: "ncFadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both" }}>
            <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
            <div className="p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4" style={{ color: accent }} />
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">How the community works · {tourStep + 1}/{TOUR_STEPS.length}</p>
              </div>
              <h3 data-testid="club-tour-title" className="font-bold uppercase tracking-[0.12em] text-lg text-zinc-50">{TOUR_STEPS[tourStep].title}</h3>
              <p data-testid="club-tour-body" className="font-mono text-[11px] text-zinc-400 leading-relaxed">{TOUR_STEPS[tourStep].body}</p>
              <div className="flex items-center gap-1.5">
                {TOUR_STEPS.map((_, i) => (
                  <span key={i} className="h-1 flex-1 transition-colors" style={{ backgroundColor: i <= tourStep ? accent : "rgba(255,255,255,0.08)" }} />
                ))}
              </div>
              <div className="flex gap-2">
                <button data-testid="club-tour-next"
                  onClick={() => {
                    audio?.tick();
                    if (tourStep < TOUR_STEPS.length - 1) setTourStep(tourStep + 1);
                    else { setTourOpen(false); localStorage.setItem(TOUR_KEY, "1"); }
                  }}
                  className="flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] py-3 text-black transition-opacity hover:opacity-85"
                  style={{ backgroundColor: accent }}>
                  {tourStep < TOUR_STEPS.length - 1 ? "Next" : "Got it — take me in"}
                </button>
                <button data-testid="club-tour-skip" onClick={() => { setTourOpen(false); localStorage.setItem(TOUR_KEY, "1"); }}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 border border-white/15 text-zinc-500 hover:text-zinc-200 transition-colors">
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function NetworkView({ net, q, setQ, accent, reload }) {
  useEffect(() => { const t = setTimeout(reload, 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!net) return <p className="font-mono text-[10px] text-zinc-500 tracking-[0.2em] uppercase animate-pulse">Reading the whole network…</p>;
  const { stats, feed, wanted, leaderboard, citizens } = net;
  return (
    <div data-testid="network-view" className="flex flex-col gap-5">
      {/* network vitals */}
      <div className="grid grid-cols-3 gap-3">
        {[["Worlds", stats.servers, Users], ["Live right now", stats.live, Sparkles], ["Citizens online", stats.players, Crown]].map(([k, v, Icon]) => (
          <div key={k} className="border border-white/10 bg-black/40 p-4">
            <p className="font-bold text-2xl text-zinc-50 flex items-center gap-2"><Icon className="w-4 h-4" style={{ color: accent }} />{v}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{k}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* citizen level leaderboard — the seasoned roleplayers board */}
        <div className="border border-white/10 bg-black/40 p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] mb-3" style={{ color: accent }}>Citizen Level — network legends</p>
          <div className="flex flex-col gap-1.5">
            {leaderboard.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em]">No levels computed yet — play and the board fills in</p>}
            {leaderboard.map((u, i) => (
              <div key={u.id} data-testid={`net-legend-${i}`} className="flex items-center gap-3 px-3 py-2 border border-white/5 bg-white/[0.02]">
                <span className="font-mono text-[10px] font-bold w-6" style={{ color: MEDAL[i] || "#71717a" }}>{i + 1}</span>
                <span className="font-semibold text-xs text-zinc-100 truncate flex items-center gap-1.5">
                  {u.name}
                  {u.verified_studio && <BadgeCheckInline />}
                </span>
                {u.flair && <span className="font-mono text-[8px] text-zinc-500 truncate">{u.flair}</span>}
                <span className="ml-auto font-mono text-[9px] font-bold px-2 py-0.5 border" style={{ color: accent, borderColor: `${accent}55` }}>LVL {u.level}</span>
              </div>
            ))}
          </div>
        </div>

        {/* network most wanted */}
        <div className="border border-white/10 bg-black/40 p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-red-400 mb-3">Network most wanted</p>
          <div className="flex flex-col gap-1.5">
            {wanted.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em]">No active warrants anywhere — suspiciously quiet</p>}
            {wanted.slice(0, 6).map((w, i) => (
              <div key={`${w.pc}-${i}`} data-testid={`net-wanted-${i}`} className="flex items-center gap-3 px-3 py-2 border border-red-900/30 bg-red-950/10">
                <ShieldAlert className="w-3.5 h-3.5 text-red-400 shrink-0" />
                <span className="min-w-0">
                  <span className="block font-semibold text-xs text-zinc-100 truncate">{w.name} <span className="font-mono text-[8px] text-zinc-500">· {w.server}</span></span>
                  <span className="block font-mono text-[8px] text-zinc-500 truncate">{w.reason}</span>
                </span>
                <span className="ml-auto font-mono text-[9px] font-bold text-amber-400 shrink-0">${w.bounty.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* cross-server directory */}
      <div>
        <div className="relative w-72 mb-3">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="net-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="SEARCH EVERY SERVER…"
            className="w-full bg-black/50 border border-white/15 focus:border-white/40 outline-none pl-9 pr-3 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-200 placeholder:text-zinc-600" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
          {citizens.map((c, i) => (
            <div key={`${c.server_id}-${c.pc}`} data-testid={`net-citizen-${i}`} className="border border-white/10 bg-[#080a0f]/85 p-3">
              <p className="font-semibold text-xs text-zinc-100 truncate">{c.name}</p>
              <p className="font-mono text-[8px] text-zinc-500 mt-0.5 truncate">{c.server} · #{c.pc}</p>
              <p className="font-mono text-[8px] text-zinc-600 mt-0.5">seen {ago(c.last_seen)}</p>
            </div>
          ))}
          {citizens.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] col-span-full">No citizens match across the network</p>}
        </div>
      </div>

      {/* cross-server wire */}
      <div className="border border-white/10 bg-black/40 p-4">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-3">The wire — every city at once</p>
        <div className="flex flex-col gap-1.5">
          {feed.slice(0, 14).map((e) => {
            const meta = WIRE_META[e.kind] || WIRE_META.citizen;
            return (
              <p key={e.id} data-testid={`net-wire-${e.id.slice(0, 6)}`} className="font-mono text-[9px] text-zinc-400 leading-relaxed flex items-baseline gap-2">
                <meta.Icon className="w-3 h-3 shrink-0 translate-y-0.5" style={{ color: meta.color }} />
                <span className="text-zinc-600 shrink-0">[{e.server}]</span>
                <span className="flex-1">{e.text}</span>
                <span className="text-zinc-600 shrink-0">{ago(e.at)}</span>
              </p>
            );
          })}
          {feed.length === 0 && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em]">The network is quiet</p>}
        </div>
      </div>
    </div>
  );
}

const BadgeCheckInline = () => (
  <span data-testid="verified-studio" title="SpecPixel Studio Staff" className="inline-flex items-center px-1 py-px border border-sky-400/50 text-sky-400 font-mono text-[7px] uppercase tracking-[0.1em]">✓ Studio</span>
);
